import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppointmentType, ScheduleType } from '../../types';
import { PatientTypeEnum } from '../../types';
import type { InsuranceDetails, CreateAppointmentPayload } from '../../services/api';

export type PatientType = PatientTypeEnum | '';

export interface PatientDetails {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  notes?: string;
  id?: string;
  birth_date?: string;
  paymentType?: 'insurance' | 'myself' | null;
  insuranceDetails?: {
    provider?: string;
    policyHolderName?: string;
    policyHolderDob?: string;
    memberId?: string;
  };
  smsConsent?: boolean;
}

export interface ExistingPatientMatch {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birth_date: string;
}

export const resolvePatient = (
  patientDetails: PatientDetails,
  match: ExistingPatientMatch | null
): PatientDetails => {
  if (!match) return patientDetails;
  return {
    ...patientDetails,
    firstName: match.firstName,
    lastName: match.lastName,
    email: match.email,
    phone: match.phone || patientDetails.phone,
    birth_date: match.birth_date || patientDetails.birth_date,
    id: match.id || patientDetails.id,
  };
};

// Attaches the patient to the appointment. An existing patient (has a DB id from
// search) links via the flat `patient_id`; a new patient sends `patient_attributes`.
// Never both — the backend rejects patient_attributes that contain an id.
export const buildPatientLinkage = (
  patient: PatientDetails
): Pick<CreateAppointmentPayload, 'patient_id' | 'patient_attributes'> => {
  if (patient.id) {
    return { patient_id: patient.id };
  }
  return {
    patient_attributes: {
      first_name: patient.firstName,
      last_name: patient.lastName,
      email: patient.email,
      mobile_phone: patient.phone,
      birth_date: patient.birth_date,
      status: 'active',
    },
  };
};

// Maps the Redux insurance form shape into the JSONB `insurance_details` blob.
export const buildInsuranceDetails = (
  insuranceDetails: PatientDetails['insuranceDetails']
): InsuranceDetails => ({
  insurance_carrier_name: insuranceDetails?.provider || '',
  insurance_member_id: insuranceDetails?.memberId || '',
  policy_holder_name: insuranceDetails?.policyHolderName || '',
  policy_holder_dob: insuranceDetails?.policyHolderDob || '',
});

export interface AppointmentTimePreference {
  preferred_date: string;
  start_time: string | null;
  end_time: string | null;
  priority: number;
  any_time?: boolean;
}

export const createEmptyPatientDetails = (): PatientDetails => ({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  notes: '',
  birth_date: '',
  paymentType: null,
  insuranceDetails: {
    provider: '',
    policyHolderName: '',
    policyHolderDob: '',
    memberId: '',
  },
  smsConsent: false,
});

interface AppointmentState {
  treatment: AppointmentType | null;
  patientType: PatientType;
  selectedSlot: string | null;
  patientDetails: PatientDetails;
  existingPatientMatch: ExistingPatientMatch | null;
  selectedPmsOperatoryId?: string | null;
  selectedDurationMinutes?: number | null;
  selectedOperatories?: string[];
  primetimeFilter: ScheduleType;
  preferredTimeSlots: AppointmentTimePreference[];
  viewMode: 'table' | 'list' | null;
  slotsFetchDurationOverride: number | null;
  isFirstSlotsFetch: boolean;
}

const initialState: AppointmentState = {
  treatment: null,
  patientType: '',
  selectedSlot: null,
  patientDetails: createEmptyPatientDetails(),
  existingPatientMatch: null,
  selectedPmsOperatoryId: null,
  selectedDurationMinutes: null,
  selectedOperatories: [],
  primetimeFilter: 'evenings_and_weekends',
  preferredTimeSlots: [],
  viewMode: null,
  slotsFetchDurationOverride: null,
  isFirstSlotsFetch: true,
};

const appointmentSlice = createSlice({
  name: 'appointment',
  initialState,
  reducers: {
    setTreatment(state, action: PayloadAction<AppointmentType>) {
      state.treatment = action.payload;
      // Reset downstream selections when treatment changes
      state.selectedSlot = null;
      state.selectedPmsOperatoryId = null;
      // Recompute duration if patient type already chosen
      if (state.patientType && action.payload) {
        state.selectedDurationMinutes =
          state.patientType === 'new'
            ? action.payload.newPatientMinutes
            : action.payload.existingPatientMinutes;
      } else {
        state.selectedDurationMinutes = null;
      }
    },
    setExistingPatientMatch(state, action: PayloadAction<ExistingPatientMatch | null>) {
      state.existingPatientMatch = action.payload;
    },
    setPatientType(state, action: PayloadAction<PatientType>) {
      if (state.patientType !== action.payload) {
        state.patientDetails = createEmptyPatientDetails();
        state.existingPatientMatch = null;
        state.selectedSlot = null;
        state.selectedPmsOperatoryId = null;
        state.selectedOperatories = [];
        state.preferredTimeSlots = [];
        state.slotsFetchDurationOverride = null;
        state.isFirstSlotsFetch = true;
      }
      state.patientType = action.payload;
      if (state.treatment) {
        state.selectedDurationMinutes =
          action.payload === 'new'
            ? state.treatment.newPatientMinutes
            : state.treatment.existingPatientMinutes;
      } else {
        state.selectedDurationMinutes = null;
      }
    },
    setSelectedSlot(state, action: PayloadAction<string | null>) {
      state.selectedSlot = action.payload;
    },
    setSelectedPmsOperatoryId(state, action: PayloadAction<string | null>) {
      state.selectedPmsOperatoryId = action.payload;
    },
    setSelectedOperatories(state, action: PayloadAction<string[]>) {
      state.selectedOperatories = action.payload
    },
    setPatientDetails(state, action: PayloadAction<PatientDetails>) {
      state.patientDetails = action.payload;
    },
    setPrimetimeFilter(state, action: PayloadAction<ScheduleType>) {
      state.primetimeFilter = action.payload;
      state.selectedSlot = null; // Clear selected slot when filter changes
    },
    setPreferredTimeSlots(state, action: PayloadAction<AppointmentTimePreference[]>) {
      state.preferredTimeSlots = action.payload;
    },
    setViewMode(state, action: PayloadAction<'table' | 'list' | null>) {
      state.viewMode = action.payload;
    },
    setSlotsFetchDurationOverride(state, action: PayloadAction<number | null>) {
      state.slotsFetchDurationOverride = action.payload;
    },
    setIsFirstSlotsFetch(state, action: PayloadAction<boolean>) {
      state.isFirstSlotsFetch = action.payload;
    },
    resetAppointment(state) {
      state.treatment = null;
      state.patientType = '';
      state.selectedSlot = null;
      state.patientDetails = createEmptyPatientDetails();
      state.existingPatientMatch = null;
      state.selectedPmsOperatoryId = null;
      state.selectedDurationMinutes = null;
      state.selectedOperatories = [];
      state.primetimeFilter = 'evenings_and_weekends';
      state.preferredTimeSlots = [];
      state.viewMode = null;
      state.slotsFetchDurationOverride = null;
      state.isFirstSlotsFetch = true;
    },
  },
});

export const {
  setTreatment,
  setPatientType,
  setSelectedSlot,
  setSelectedPmsOperatoryId,
  setSelectedOperatories,
  setPatientDetails,
  setExistingPatientMatch,
  setPrimetimeFilter,
  resetAppointment,
  setPreferredTimeSlots,
  setViewMode,
  setSlotsFetchDurationOverride,
  setIsFirstSlotsFetch,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;





